// frontend/src/modules/payroll/components/PayrollSummaryStrip.tsx
//
// A 4-tile KPI strip used at the top of the Payroll runs page and the
// Reports page. Sums are computed from real run + payslip data — never
// fabricated. All money values go through formatMoney().
import * as React from 'react';
import { motion } from 'framer-motion';
import { Banknote, Users, Wallet, Clock4 } from 'lucide-react';
import { VISUAL } from '../../../design-system/visual';
import { formatMoney } from '../../../design-system/format';
import { useBranding } from '../../../core/branding/BrandingProvider';
import type { Payslip, PayrollRun } from '../types';

interface Props {
  /** Payslips with their parent run attached, as returned by `listAllPayslips`. */
  rows: Array<Payslip & { run: PayrollRun }>;
  loading?: boolean;
}

export function PayrollSummaryStrip({ rows, loading = false }: Props) {
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';

  // "This month" = period_start falls inside the current calendar month.
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const monthRows = rows.filter((r) => {
    if (!r.run?.period_start) return false;
    const d = new Date(r.run.period_start);
    return d.getFullYear() === y && d.getMonth() === m;
  });

  const monthSpend = monthRows.reduce((s, r) => s + Number(r.net_amount), 0);
  const employees = new Set(rows.map((r) => r.employee_id)).size;
  const totalSpend = rows.reduce((s, r) => s + Number(r.net_amount), 0);
  const avgPerEmployee = employees > 0 ? totalSpend / employees : 0;
  const pending = rows.filter((r) => !r.delivered_at).length;

  const tiles: Tile[] = [
    {
      label: 'This month',
      value: formatMoney(monthSpend, currency),
      caption: `${monthRows.length} payslip${monthRows.length === 1 ? '' : 's'} · ${now.toLocaleString(undefined, { month: 'long' })}`,
      icon: <Banknote className="h-3.5 w-3.5" />,
      tone: 'saffron',
    },
    {
      label: 'Employees paid',
      value: String(employees),
      caption: `Across ${rows.length} payslip${rows.length === 1 ? '' : 's'}`,
      icon: <Users className="h-3.5 w-3.5" />,
      tone: 'neutral',
    },
    {
      label: 'Avg per employee',
      value: formatMoney(avgPerEmployee, currency),
      caption: 'Lifetime, all runs',
      icon: <Wallet className="h-3.5 w-3.5" />,
      tone: 'neutral',
    },
    {
      label: 'Pending payslips',
      value: String(pending),
      caption: pending === 0 ? 'All delivered' : 'Awaiting email delivery',
      icon: <Clock4 className="h-3.5 w-3.5" />,
      tone: pending > 0 ? 'warning' : 'success',
    },
  ];

  return (
    <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {tiles.map((t, i) => (
        <Kpi key={t.label} {...t} loading={loading} delay={i * 0.05} />
      ))}
    </section>
  );
}

interface Tile {
  label: string;
  value: React.ReactNode;
  caption: string;
  icon: React.ReactNode;
  tone: 'neutral' | 'success' | 'saffron' | 'warning';
}

function Kpi({ label, value, caption, icon, tone, loading, delay }: Tile & { loading?: boolean; delay?: number }) {
  const toneClass =
    tone === 'success' ? 'bg-success/10 text-success ring-success/25 dark:bg-success/15' :
    tone === 'saffron' ? 'bg-saffron/15 text-saffron-foreground ring-saffron/30' :
    tone === 'warning' ? 'bg-coral/12 text-coral-700 ring-coral/25 dark:text-coral dark:bg-coral/15' :
                         'bg-muted text-muted-foreground ring-border/50';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 130, damping: 22, delay }}
      className={[VISUAL.bento, 'p-5'].join(' ')}
    >
      <div className="flex items-center justify-between mb-3">
        <p className={VISUAL.eyebrow}>{label}</p>
        <span className={['inline-flex h-8 w-8 items-center justify-center rounded-lg ring-1', toneClass].join(' ')}>{icon}</span>
      </div>
      {loading ? (
        <div className="h-8 w-3/4 rounded bg-muted/60 animate-pulse" />
      ) : (
        <p className="font-sans font-semibold text-[clamp(1.4rem,2.2vw,2rem)] leading-none tracking-[-0.03em] tabular-nums text-foreground">
          {value}
        </p>
      )}
      <p className="text-[12px] text-muted-foreground mt-2">{caption}</p>
    </motion.div>
  );
}
